import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { ChevronRight } from 'lucide-react';
import { jsonLd } from '../lib/jsonLd';

const faqs = [
  {
    question: 'What services does Southeast Asia Luxury Yachts offer?',
    answer: 'We provide a comprehensive yacht marketplace featuring luxury vessels for charter and sale across Southeast Asia. Our services include yacht discovery, booking coordination, expert guides, and personalized concierge support to help you find and enjoy the perfect yacht experience.'
  },
  {
    question: 'Which destinations do you cover?',
    answer: 'We specialize in Southeast Asia\'s premier yachting destinations including Thailand (Phuket, Andaman Sea), Indonesia (Raja Ampat, Komodo, Bali), Malaysia (Langkawi), Philippines (Palawan), Myanmar (Mergui Archipelago), and Singapore. Each destination offers unique cruising experiences.'
  },
  {
    question: 'How do I charter a yacht through your platform?',
    answer: 'Browse our yacht listings, use our Yacht Finder quiz to get personalized recommendations, or contact our team directly. Once you\'ve selected a vessel, we\'ll coordinate with the yacht owner/manager to arrange your charter, handle documentation, and ensure a seamless experience.'
  },
  {
    question: 'What is included in a typical yacht charter?',
    answer: 'Most charters include the yacht, professional crew, standard equipment, and insurance. Additionally, you\'ll budget for APA (Advance Provisioning Allowance) covering fuel, food, drinks, and docking fees - typically 25-35% of the charter fee. Gratuity for crew is customary (10-20%).'
  },
  {
    question: 'Do I need yachting experience to charter?',
    answer: 'No experience required for crewed charters - professional captains and crew handle all sailing and navigation. For bareboat (self-skippered) charters, valid sailing certifications and demonstrated experience are required. Most of our clients choose crewed charters for the full luxury experience.'
  },
];

export default function About() {
  usePageTitle('About Us');
  const [expandedFaq, setExpandedFaq] = useState(0);

  // Schema.org JSON-LD for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  
  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(faqSchema) }} />

      {/* Hero */}
      <div className="mb-20">
        <h1
          className="text-5xl md:text-6xl font-medium tracking-tight mb-6"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="about-title"
        >
          About <span className="text-gold">Southeast Asia Luxury Yachts</span>
        </h1>
        <p className="text-xl text-white/70 leading-relaxed max-w-3xl">
          Your authoritative gateway to superyacht excellence across Southeast Asia's most exclusive waters.
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
        <div>
          <h2
            className="text-3xl font-medium mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Our Mission
          </h2>
          <p className="text-white/70 leading-relaxed mb-6">
            Southeast Asia Luxury Yachts was founded with a singular vision: to become the definitive resource for superyacht ownership, chartering, and maritime expertise in the region's waters.
          </p>
          <p className="text-white/70 leading-relaxed mb-6">
            From the limestone karsts of Thailand's Andaman Sea to the pristine reefs of Raja Ampat, from Myanmar's untouched Mergui Archipelago to the Philippines' hidden lagoons — we connect discerning clients with exceptional vessels and unparalleled knowledge.
          </p>
          <p className="text-white/70 leading-relaxed">
            Our platform combines a carefully curated marketplace with magazine-quality editorial content, offering both transactional efficiency and educational depth for the modern yacht enthusiast.
          </p>
        </div>

        <div className="aspect-[4/3] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1598448056086-307e98ef5c4a?w=800&q=85"
            alt="Luxury yacht interior"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Values */}
      <div className="mb-20">
        <h2
          className="text-3xl font-medium mb-12 text-center"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Our Values
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: 'Expertise',
              description: 'Deep regional knowledge backed by years of maritime experience and local partnerships across Southeast Asia.',
            },
            {
              title: 'Curation',
              description: 'Every yacht in our collection is vetted for quality, ensuring our clients access only the finest vessels.',
            },
            {
              title: 'Service',
              description: 'White-glove support throughout your journey, from initial inquiry to setting sail on your dream yacht.',
            },
          ].map((value, idx) => (
            <div key={idx} className="glass-light p-8" data-testid={`value-card-${idx}`}>
              <h3 className="text-gold text-xl font-semibold mb-4 uppercase tracking-wide">{value.title}</h3>
              <p className="text-white/70 leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Region Expertise */}
      <div className="mb-20">
        <h2
          className="text-3xl font-medium mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Regional Expertise
        </h2>
        <p className="text-white/70 leading-relaxed mb-8">
          Southeast Asia presents unique opportunities and challenges for yacht owners and charterers. Our team understands the complexities of navigating these diverse waters:
        </p>
        <ul className="space-y-4 text-white/70">
          <li className="flex items-start gap-3">
            <span className="text-gold mt-1">•</span>
            <span><strong className="text-white">Permit & Documentation:</strong> Comprehensive knowledge of cruising permits, customs procedures, and flag state regulations across Thailand, Indonesia, Malaysia, Myanmar, Philippines, Vietnam, and Singapore.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-gold mt-1">•</span>
            <span><strong className="text-white">Seasonal Planning:</strong> Expert guidance on monsoon patterns, optimal cruising windows, and weather routing for safe, comfortable passages.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-gold mt-1">•</span>
            <span><strong className="text-white">Infrastructure:</strong> Connections to marinas, haul-out facilities, provisioning hubs, and technical support throughout the region.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-gold mt-1">•</span>
            <span><strong className="text-white">Cultural Insights:</strong> Respect for local customs, environmental considerations, and community engagement in sensitive marine areas.</span>
          </li>
        </ul>
      </div>

      {/* FAQ Section */}
      <div className="mb-20" data-testid="about-faq-section">
        <h2
          className="text-3xl font-medium mb-8 text-center"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Frequently Asked Questions
        </h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, idx) => (
            <div key={idx} className="glass-light">
              <button
                onClick={() => setExpandedFaq(expandedFaq === idx ? -1 : idx)}
                className="w-full p-6 text-left flex justify-between items-center"
                data-testid={`about-faq-${idx}`}
              >
                <span className="font-medium text-lg pr-4">{faq.question}</span>
                <ChevronRight 
                  size={20} 
                  className={`text-gold shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} 
                />
              </button>
              {expandedFaq === idx && (
                <div className="px-6 pb-6 text-white/70 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="glass-heavy p-12 text-center">
        <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Ready to Find Your Perfect Yacht?
        </h2>
        <p className="text-white/70 mb-8 max-w-2xl mx-auto">
          Take our quick quiz to discover yachts tailored to your preferences, or browse our full collection.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/yacht-finder">
            <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
              Take the Yacht Finder Quiz
            </Button>
          </Link>
          <Link to="/listings">
            <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">
              Browse All Yachts
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
